// @flow
import * as React from 'react';
import styled from 'styled-components';
import invariant from 'invariant';
import type { FileUploadTypes } from 'api/shared';
import Box from 'components/Box';
import { isNotEmpty, partial, entries } from 'utils/functional';

import { getJson, postData, postJson } from 'utils/http';
import { waitTaskCompleted } from 'utils/asyncBackendTask';
import { configValue } from 'utils/config';
import { fileIdToUrl } from 'utils/s3file';

const { useEffect, useState, useCallback, useRef } = React;

const InputContainer = styled(Box)`
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const objToFormData = (o: Object): FormData => {
  const f = new FormData();
  entries(o).forEach(([k, v]) => f.append(k, v));
  return f;
};

const uploadPresignedUrl = (f: File, r: Object) => {
  const { params, s3File } = r;
  const formData = objToFormData(params.fields);
  formData.append('file', f);
  return postData(params.url, formData).then(() =>
    postJson(`/data/s3-uploads/${s3File.id}/done`).then(({ task }) =>
      waitTaskCompleted(task.url).then(() => s3File.id)
    )
  );
};

const useMock = configValue('idealist', 'fileUploadMock');

const uploadToS3 = (f: File, fileType: FileUploadTypes) =>
  postJson('/data/s3-uploads', {
    fileType,
    filename: f.name,
    size: f.size, // bytes
    mimeType: f.type
  }).then(partial(uploadPresignedUrl, f));

const S3Picker = ({
  children,
  name,
  downloadAttachment,
  isRequired,
  qaId,
  fileId,
  fileType,
  onChange
}: {
  children: Object => React.Node,
  downloadAttachment?: boolean,
  name: string,
  isRequired: boolean,
  qaId: string,
  fileId: ?string,
  fileType: FileUploadTypes,
  onChange: (?string) => void
}) => {
  const inputRef = useRef<?HTMLInputElement>(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [filename, setFilename] = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);

  const onMouseEnter = () => {
    setMouseActive(true);
  };

  const onMouseLeave = () => {
    setMouseActive(false);
  };

  const onDragEnter = () => {
    setDragActive(true);
    setMouseActive(true);
  };

  const onDragEnd = () => {
    setDragActive(false);
    setMouseActive(false);
  };

  const onClick = () => {
    if (!useMock && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onFileChange = useCallback(
    () => {
      const fs = inputRef.current && inputRef.current.files;
      if (isNotEmpty(fs)) {
        invariant(fs, 'fs must be defined'); // for flow
        const f = fs[0];
        setLoading(true);
        uploadToS3(f, fileType)
          .then(newFileId => {
            setFilename(f.name);
            setCreated(new Date());
            setUrlPreview(URL.createObjectURL(f));
            onChange(newFileId);
          })
          .finally(() => {
            setLoading(false);
            setDragActive(false);
            setMouseActive(false);
          });
      }
    },
    [fileType, onChange]
  );

  useEffect(
    () => {
      if (fileId && !filename) {
        setLoading(true);
        getJson(`/data/s3-uploads/${fileId}/meta`)
          .then(({ s3File }) => {
            setFilename(s3File.filename);
            setCreated(s3File.created);
          })
          .finally(() => {
            setLoading(false);
          });
      }
      if (fileId && !urlPreview) {
        setUrlPreview(fileIdToUrl(fileId, downloadAttachment));
      }
    },
    [fileId, urlPreview, filename, downloadAttachment]
  );

  return (
    <InputContainer data-qa-id={qaId}>
      <input
        ref={inputRef}
        required={isRequired}
        name={name}
        type="file"
        onChange={onFileChange}
        data-qa-id={`${qaId}-hidden-input`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragEnter={onDragEnter}
        onDragEnd={onDragEnd}
        onDragLeave={onDragEnd}
      />
      {children({
        onClick,
        urlPreview,
        filename,
        loading,
        dragActive,
        mouseActive,
        created
      })}
    </InputContainer>
  );
};

export default S3Picker;
